import { Calendar } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { OnlineBookingConfig } from "../hooks/use-online-booking-config";

export function AdvanceBookingConfig() {
  const { control } = useFormContext<OnlineBookingConfig>();
  const { t } = useTranslation("settings");
  const advanceBookingDays = useWatch({
    control,
    name: "advanceBookingDays",
  });

  const getBookingPeriodText = (days: number) => {
    if (days === 1) return t("onlineBooking.tomorrow");
    if (days === 7) return t("onlineBooking.oneWeek");
    if (days === 14) return t("onlineBooking.twoWeeks");
    if (days === 30) return t("onlineBooking.oneMonth");
    if (days === 60) return t("onlineBooking.twoMonths");
    if (days === 90) return t("onlineBooking.threeMonths");
    return `${days} ${t("onlineBooking.days")}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        <h4 className="font-medium">{t("bookingSettings.advanceBooking")}</h4>
      </div>

      <FormField
        control={control}
        name="advanceBookingDays"
        rules={{
          required: t("validation.advanceBookingRequired"),
          min: { value: 1, message: t("validation.advanceBookingMinDays") },
          max: { value: 365, message: t("validation.advanceBookingMaxDays") },
        }}
        render={({ field }) => (
          <FormItem>
            <SettingField
              label={t("onlineBooking.fields.advanceBooking.label")}
              helpText={t("onlineBooking.fields.advanceBooking.helpText")}
              helpDetails={
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">
                    {t("onlineBooking.fields.advanceBooking.helpDetails.title")}
                  </div>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    {(
                      t("onlineBooking.fields.advanceBooking.helpDetails.benefits", {
                        returnObjects: true,
                      }) as string[]
                    ).map((benefit: string, index: number) => (
                      <li key={index}>• {benefit}</li>
                    ))}
                  </ul>
                </div>
              }
            >
              <div className="flex items-center gap-2">
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    className="w-24"
                    {...field}
                    onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 1)}
                  />
                </FormControl>
                <span className="text-sm text-muted-foreground">{t("onlineBooking.days")}</span>
              </div>
            </SettingField>
            <FormMessage />
          </FormItem>
        )}
      />

      {advanceBookingDays && (
        <p className="text-xs text-muted-foreground">
          {t("onlineBooking.bookingPeriodInfo", {
            period: getBookingPeriodText(advanceBookingDays),
          })}
        </p>
      )}
    </div>
  );
}
